import { useState, useEffect, useMemo } from "react";
import { ResponsivePie } from "@nivo/pie";
import { useTheme, Box, Typography, CircularProgress, useMediaQuery } from "@mui/material";
import { tokens } from "../../theme";
import axios from "axios";

const SpsDistributionChart = ({ viewMode = "total", isFullscreen = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [spsData, setSpsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rawData, setRawData] = useState(null);
  
  // Media queries für responsive Design
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isVerySmallScreen = useMediaQuery(theme.breakpoints.down('xs'));

  // Dynamisch angepasste Margin und andere Eigenschaften basierend auf der Bildschirmgröße
  const chartMargins = useMemo(() => {
    if (isVerySmallScreen) {
      return { top: 10, right: 10, bottom: 30, left: 10 };
    } else if (isMobile) {
      return { top: 20, right: 70, bottom: 40, left: 70 };
    } else if (isFullscreen) {
      // Größere Margins im Fullscreen-Modus
      return { top: 60, right: 180, bottom: 100, left: 180 };
    } else {
      return { top: 40, right: 150, bottom: 60, left: 150 };
    }
  }, [isMobile, isVerySmallScreen, isFullscreen]);

  // Angepasste Pie-Parameter für mobile Geräte und Fullscreen
  // Erhöhter skipAngle-Wert für kleine Segmente
  const arcLinkLabelsSkipAngle = useMemo(() => {
    if (viewMode === "reward_pools") {
      return isMobile ? 8 : isFullscreen ? 0 : 3;
    }
    return isMobile ? 5 : isFullscreen ? 0 : 0;
  }, [isMobile, viewMode, isFullscreen]);
  
  const arcLabelsSkipAngle = useMemo(() => {
    if (viewMode === "reward_pools") {
      return isMobile ? 15 : isFullscreen ? 0 : 5;
    }
    return isMobile ? 10 : isFullscreen ? 0 : 0;
  }, [isMobile, viewMode, isFullscreen]);
  
  const innerRadius = useMemo(() => {
    if (isFullscreen) return 0.5;
    return isMobile ? 0.52 : 0.55;
  }, [isMobile, isFullscreen]);
  const padAngle = useMemo(() => isMobile ? 0.5 : 0.8, [isMobile]);
  const cornerRadius = useMemo(() => isMobile ? 2 : 3, [isMobile]);

  // Descriptions for each category
  const categoryDescriptions = {
    total_staked: "Locked in staking by users",
    circulating_supply: "Available for trading/use",
    off_chain: "On other blockchains (ETH, BSC)",
    reserve: "Reserved for future use by the DAO",
    reward_pools: "Allocated to reward programs",
    burned: "Permanently removed from circulation",
    unstaking: "In unstaking process (part of staked SPS)"
  };

  // Hilfsfunktion zur Formatierung großer Zahlen - noch kompakter für mobile
  const formatCompactValue = (value) => {
    if (isVerySmallScreen) {
      // Extra kompaktes Format für sehr kleine Bildschirme
      if (value >= 1000000000) {
        return `${(value / 1000000000).toFixed(0)}B`;
      } else if (value >= 1000000) {
        return `${(value / 1000000).toFixed(0)}M`;
      } else if (value >= 1000) {
        return `${(value / 1000).toFixed(0)}K`;
      }
    } else if (isMobile) {
      // Kompaktes Format für mobile Geräte
      if (value >= 1000000000) {
        return `${(value / 1000000000).toFixed(1)}B`;
      } else if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)}M`;
      } else if (value >= 1000) {
        return `${(value / 1000).toFixed(0)}K`;
      }
    } else {
      // Standard Format für Desktop
      if (value >= 1000000000) {
        return `${(value / 1000000000).toFixed(1)}B`;
      } else if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)}M`;
      } else if (value >= 1000) {
        return `${(value / 1000).toFixed(1)}K`;
      }
    }
    return Math.round(value).toString();
  };

  useEffect(() => {
    const fetchSpsData = async () => {
      try {
        setLoading(true);
        
        // Fetch from backend API which includes off-chain breakdown
        const backendResponse = await axios.get("https://api.elmo-burns.com/api/v1/sps-distribution-history/?ordering=-date&page_size=1");
        const backendData = backendResponse.data.results[0];
        
        // Also fetch validator API for real-time data
        const validatorResponse = await axios.get(
          "https://splinterlands-validator-api.splinterlands.com/extensions/tokens/SPS/supply?"
        );
        setRawData(validatorResponse.data);
        
        const { total_staked, off_chain, reserve, reward_pools, burned, minted, unstaking } = validatorResponse.data;
        
        const totalBasis = minted;
        const burnedAmount = burned;
        const rewardPoolsAmount = reward_pools.total;
        const reserveTotalAmount = reserve.total;
        const unstakingAmount = unstaking.pending_unstake + unstaking.pending_immediate_unstake;
        const stakedAmount = total_staked - unstakingAmount;
        
        // Get player off-chain SPS from backend (convert string to number)
        const playerOffchainAmount = parseFloat(backendData.player_offchain_total) || 0;
        const daoOffchainAmount = parseFloat(backendData.dao_offchain_total) || 0;
        const offchainTotalAmount = off_chain.total;
        
        // Reserve contains ALL off-chain tokens (DAO + Player)!
        // Reserve on-chain = reserve.total - off_chain.total
        const reserveOnchainAmount = reserveTotalAmount - offchainTotalAmount;
        
        // Liquid on-chain = minted - burned - staked - unstaking - reserve - reward_pools
        const liquidOnchainAmount = Math.max(0, totalBasis - burnedAmount - rewardPoolsAmount - reserveTotalAmount - stakedAmount - unstakingAmount);
        
        const calculatePercentage = (value) => ((value / totalBasis) * 100).toFixed(1);
        
        const getMobileFriendlyId = (id) => {
          if (isMobile) {
            switch(id) {
              case 'Burned': return 'Burn';
              case 'Staked': return 'Stake';
              case 'Liquid (On-chain)': return 'L-On';
              case 'Liquid (Off-chain)': return 'L-Off';
              case 'DAO Reserve (On-chain)': return 'DAO';
              case 'DAO Off-chain': return 'DAO-Off';
              case 'Reward Pools': return 'Rewards';
              case 'Unstaking': return 'Unstk';
              default: return id;
            }
          }
          return id;
        };
        
        // --- TOTAL DATA ---
        const totalDistributionData = [
            {
            id: "DAO Off-chain",
            label: getMobileFriendlyId("DAO Off-chain"),
            value: daoOffchainAmount,
            percentage: calculatePercentage(daoOffchainAmount),
            description: "DAO SPS on other blockchains (in DEX pools)",
            details: `${formatValue(daoOffchainAmount)} SPS tokens from DAO on BSC/Base/ETH in liquidity pools (${calculatePercentage(daoOffchainAmount)}% of total minted)`,
            color: totalColorMap[getMobileFriendlyId("DAO Off-chain")] || nivoPieColors[7]
          },
          {
            id: "Burned",
            label: getMobileFriendlyId("Burned"),
            value: burnedAmount,
            percentage: calculatePercentage(burnedAmount),
            description: categoryDescriptions.burned,
            details: `${formatValue(burnedAmount)} SPS tokens have been permanently burned (${calculatePercentage(burnedAmount)}% of total minted)`,
            color: totalColorMap[getMobileFriendlyId("Burned")] || nivoPieColors[0]
          },
          {
            id: "Unstaking",
            label: getMobileFriendlyId("Unstaking"),
            value: unstakingAmount,
            percentage: calculatePercentage(unstakingAmount),
            description: categoryDescriptions.unstaking,
            details: `${formatValue(unstakingAmount)} SPS tokens in unstaking process - still part of staked SPS (${calculatePercentage(unstakingAmount)}% of total minted)`,
            color: totalColorMap[getMobileFriendlyId("Unstaking")] || nivoPieColors[2]
          },
          {
            id: "Player Liquid (On-chain)",
            label: getMobileFriendlyId("Player Liquid (On-chain)"),
            value: liquidOnchainAmount,
            percentage: calculatePercentage(liquidOnchainAmount),
            description: "Player SPS tokens on Hive blockchain (not staked)",
            details: `${formatValue(liquidOnchainAmount)} SPS tokens held by players on Hive (${calculatePercentage(liquidOnchainAmount)}% of total minted)`,
            color: totalColorMap[getMobileFriendlyId("Player Liquid (On-chain)")] || nivoPieColors[3]
          },
          {
            id: "Player Liquid (Off-chain)",
            label: getMobileFriendlyId("Player Liquid (Off-chain)"),
            value: playerOffchainAmount,
            percentage: calculatePercentage(playerOffchainAmount),
            description: "Player SPS on other blockchains (BSC, Base, ETH, Hive-Engine)",
            details: `${formatValue(playerOffchainAmount)} SPS tokens held by players on BSC, Base, ETH, Hive-Engine (${calculatePercentage(playerOffchainAmount)}% of total minted)`,
            color: totalColorMap[getMobileFriendlyId("Player Liquid (Off-chain)")] || nivoPieColors[4]
          },
            {
            id: "Staked",
            label: getMobileFriendlyId("Staked"),
            value: stakedAmount,
            percentage: calculatePercentage(stakedAmount),
            description: categoryDescriptions.total_staked,
            details: `${formatValue(stakedAmount)} SPS tokens are staked by users (${calculatePercentage(stakedAmount)}% of total minted)`,
            color: totalColorMap[getMobileFriendlyId("Staked")] || nivoPieColors[1]
          },
          {
            id: "Reward Pools",
            label: getMobileFriendlyId("Reward Pools"),
            value: rewardPoolsAmount,
            percentage: calculatePercentage(rewardPoolsAmount),
            description: categoryDescriptions.reward_pools,
            details: `${formatValue(rewardPoolsAmount)} SPS tokens in reward pools (${calculatePercentage(rewardPoolsAmount)}% of total minted)`,
            color: totalColorMap[getMobileFriendlyId("Reward Pools")] || nivoPieColors[5]
          },
           {
            id: "DAO Reserve (On-chain)",
            label: getMobileFriendlyId("DAO Reserve (On-chain)"),
            value: reserveOnchainAmount,
            percentage: calculatePercentage(reserveOnchainAmount),
            description: "DAO Reserve on Hive blockchain",
            details: `${formatValue(reserveOnchainAmount)} SPS tokens in DAO reserve on Hive (${calculatePercentage(reserveOnchainAmount)}% of total minted)`,
            color: totalColorMap[getMobileFriendlyId("DAO Reserve (On-chain)")] || nivoPieColors[4]
          },
        ];
        // --- REWARD POOLS DATA ---
        const rewardPoolsDataRaw = [];
        const shortNameMap = {
          "$UNCLAIMED_UNISWAP_REWARDS": "Uniswap",
          "$REWARD_POOLS_LAND": "Land",
          "$REWARD_POOLS_BRAWL": "Brawl",
          "$SPS_STAKING_REWARDS": "Staking",
          "$REWARD_POOLS_LICENSE": "License",
          "$TOURNAMENTS_DISTRIBUTION": "Tournaments",
          "$VALIDATOR_REWARDS": "Validators",
          "$REWARD_POOLS_SOULKEEP": "Soulkeep",
          "$REWARD_POOLS_MODERN": "Modern",
          "$REWARD_POOLS_WILD": "Wild",
          "$REWARD_POOLS_SURVIVAL": "Survival",
          "$REWARD_POOLS_FOCUS": "Focus",
          "$REWARD_POOLS_SEASON": "Season"
        };
        const minDisplayThreshold = reward_pools.total * 0.001;
        let otherPoolsTotal = 0;
        for (const [key, value] of Object.entries(reward_pools)) {
          if (key === "total") continue;
          if (value < minDisplayThreshold) {
            // Früher: otherPoolsTotal += value; continue;
            // Jetzt: Kleine Pools einfach überspringen, nicht mehr zu "Other" addieren
            continue;
          }
          const poolPercentage = ((value / reward_pools.total) * 100).toFixed(1);
          let displayName;
          if (shortNameMap[key]) {
            displayName = shortNameMap[key];
          } else {
            let cleanName = key.replace("$REWARD_POOLS_", "").replace("$", "");
            displayName = cleanName.toLowerCase()
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
          }
          let description = "";
          if (key === "$REWARD_POOLS_BRAWL") {
            description = "Rewards for Brawl game mode tournaments and battles";
          } else if (key === "$REWARD_POOLS_LAND") {
            description = "Rewards allocated to Land gameplay and assets";
          } else if (key === "$REWARD_POOLS_LICENSE") {
            description = "Rewards for license holders";
          } else if (key === "$VALIDATOR_REWARDS") {
            description = "Rewards for network validators";
          } else if (key === "$REWARD_POOLS_SOULKEEP") {
            description = "Rewards for Soulkeep game mode";
          } else if (key === "$REWARD_POOLS_MODERN") {
            description = "Rewards for Modern format gameplay";
          } else if (key === "$REWARD_POOLS_WILD") {
            description = "Rewards for Wild format gameplay";
          } else if (key === "$REWARD_POOLS_SURVIVAL") {
            description = "Rewards for Survival game mode";
          } else if (key === "$UNCLAIMED_UNISWAP_REWARDS") {
            description = "Unclaimed rewards from Uniswap liquidity programs";
          } else if (key === "$TOURNAMENTS_DISTRIBUTION") {
            description = "Rewards for tournament participants";
          } else if (key === "$SPS_STAKING_REWARDS") {
            description = "Rewards for staking SPS tokens";
          } else if (key === "$REWARD_POOLS_FOCUS") {
            description = "Rewards for Focus game mode";
          } else if (key === "$REWARD_POOLS_SEASON") {
            description = "Rewards for seasonal play";
          } else {
            description = `${displayName} reward allocation`;
          }
          rewardPoolsDataRaw.push({
            id: displayName,
            label: displayName,
            originalId: key,
            value: value,
            percentage: poolPercentage,
            description: description,
            details: `${formatValue(value)} SPS tokens (${poolPercentage}% of reward pools)`
          });
        }
        // Entferne den Other-Pool komplett
        // if (otherPoolsTotal > 0) { ... }  // Diesen Block entfernen
        // Assign Nivo colors to reward pools
        const rewardPoolsData = getRewardPoolColors(rewardPoolsDataRaw);
        setSpsData({
          total: totalDistributionData,
          reward_pools: rewardPoolsData
        });
        setLoading(false);
      } catch (err) {
        setError("Failed to load SPS distribution data");
        setLoading(false);
      }
    };
    fetchSpsData();
  }, [isMobile, isVerySmallScreen]);

  const formatValue = (value) => {
    return new Intl.NumberFormat('en-US').format(Math.round(value));
  };

  // Erweiterte Farbpalette für 8 unterschiedliche Segmente
  const nivoPieColors = [
    '#e8c1a0', // beige - Burned
    '#f47560', // rot - Staked  
    '#ffd93d', // hellgelb - Unstaking
    '#ff9f40', // orange - Liquid On-chain
    '#4ecdc4', // türkis - Liquid Off-chain
    '#95e1d3', // mint - DAO Reserve On-chain
    '#f9ca24', // goldgelb - DAO Off-chain
    '#c7a17a', // braun - Reward Pools
    '#61cdbb', '#97e3d5', '#f1e15b', '#e8a838', '#a05195', '#d45087', '#ff7c43', '#ffa600'
  ];

  // Mapping for total view (order must match totalDistributionData)
  const totalIds = [
    'Burned', 'Staked', 'Unstaking', 'Player Liquid (On-chain)', 'Player Liquid (Off-chain)', 'DAO Reserve (On-chain)', 'DAO Off-chain', 'Reward Pools',
    'Burn', 'Stake', 'Unstk', 'P-Liq-On', 'P-Liq-Off', 'DAO', 'DAO-Off', 'Rewards' // mobile labels fallback
  ];
  const totalColorMap = {};
  totalIds.forEach((id, idx) => {
    totalColorMap[id] = nivoPieColors[idx % nivoPieColors.length];
  });

  // For reward pools: assign Nivo colors by index
  const getRewardPoolColors = (data) => {
    return data.map((d, i) => ({ ...d, color: nivoPieColors[i % nivoPieColors.length] }));
  };

  // Prepare the chart component
  const renderChart = () => {
    // Determine which data to display based on viewMode
    const displayData = spsData[viewMode] || [];

    return (
      <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
        <ResponsivePie
          data={displayData}
          colors={viewMode === 'reward_pools' ? (d) => d.data.color : (d) => totalColorMap[d.id] || nivoPieColors[0]}
          theme={{
            axis: {
              domain: {
                line: {
                  stroke: colors.grey[100],
                },
              },
              legend: {
                text: {
                  fill: colors.grey[100],
                },
              },
              ticks: {
                line: {
                  stroke: colors.grey[100],
                  strokeWidth: 1,
                },
                text: {
                  fill: colors.grey[100],
                },
              },
            },
            legends: {
              text: {
                fill: colors.grey[100],
                fontSize: isMobile ? 10 : isFullscreen ? 14 : 12,
              },
            },
            tooltip: {
              container: {
                background: colors.primary[400],
                color: colors.grey[100],
                fontSize: isFullscreen ? 14 : 12,
              },
            },
          }}
          margin={chartMargins}
          innerRadius={isMobile ? 0.52 : innerRadius} // Pie auf Mobile wieder dicker
          padAngle={padAngle}
          cornerRadius={cornerRadius}
          activeOuterRadiusOffset={isMobile ? 5 : isFullscreen ? 10 : 8}
          borderWidth={1}
          borderColor={{
            from: "color",
            modifiers: [["darker", 0.2]],
          }}
          arcLinkLabelsSkipAngle={arcLinkLabelsSkipAngle}
          arcLinkLabelsTextColor={colors.grey[100]}
          arcLinkLabelsThickness={isMobile ? 1 : isFullscreen ? 3 : 2}
          arcLinkLabelsColor={{ from: "color" }}
          arcLinkLabelsOffset={isMobile ? 0 : isFullscreen ? 16 : 10}
          arcLinkLabelsDiagonalLength={isMobile ? 10 : isFullscreen ? 28 : 20}
          arcLinkLabelsStraightLength={isMobile ? 10 : isFullscreen ? 22 : 16}
          // Mobile-optimierte Labels mit kompakteren Prozentangaben
          arcLinkLabel={d => {
            // Für sehr kleine Werte im Reward Pools-Modus nur Prozentsatz anzeigen
            if (viewMode === "reward_pools" && parseFloat(d.data.percentage) < 1.0) {
              return `${d.id}: ${d.data.percentage}%`;
            }
            
            return isMobile ? 
              `${d.id} ${d.data.percentage}%` : 
              `${d.id} (${d.data.percentage}%)`;
          }}
          // Größere Schrift im Fullscreen-Modus
          arcLinkLabelsTextProps={{
            fontSize: isMobile ? 9 : isFullscreen ? 13 : 11,
            fontWeight: 600,
          }}
          // Zeige nur die absoluten Werte im Label innerhalb des Tortenstücks
          enableArcLabels={true}
          // Alle Segmente zeigen
          arcLabel={d => {
            return formatCompactValue(d.value);
          }}
          // Positioniere die Arc-Labels näher zur Mitte für bessere Lesbarkeit
          arcLabelsRadiusOffset={isMobile ? 0.65 : 0.4}
          arcLabelsSkipAngle={arcLabelsSkipAngle}
          arcLabelsTextColor={{
            from: "color",
            modifiers: [["darker", 2]],
          }}
          // Optimiere das Styling für die Arc-Labels
          arcLabelsTextProps={{
            fontSize: isMobile ? 8 : isFullscreen ? 12 : 9,
            fontWeight: 700,
            textAlign: "center"
          }}
          // Keine Legende anzeigen
          legends={[]}
          animate={true}
          motionConfig="gentle"
          // Verbesserte Tooltip-Anzeige
          tooltip={({ datum }) => (
            <div style={{
              padding: '12px',
              background: colors.primary[400],
              color: colors.grey[100],
              fontSize: isFullscreen ? 14 : 12,
              borderRadius: '4px',
              boxShadow: '0 3px 8px rgba(0,0,0,0.3)'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                {datum.label}
              </div>
              <div>
                {formatValue(datum.value)} SPS ({datum.data.percentage}%)
              </div>
            </div>
          )}
        />
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // Return the chart component
  return renderChart();
};

export default SpsDistributionChart;